import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from './ui/dropdown-menu';
import { useLanguage } from '../contexts/LanguageContext';
import { MANAGEABLE_ROUTES } from '../data/sectionAccess';
import { ImpersonateDialog, ImpersonationBanner } from './Impersonation';
import { 
  LayoutDashboard, 
  PlusCircle, 
  History, 
  Shield, 
  User,
  Menu,
  X,
  Globe,
  LogOut,
  ChevronRight,
  Trophy,
  Megaphone,
  HardHat,
  Send,
  FileSignature,
  ShieldCheck,
  BookOpen,
  DollarSign,
  CalendarDays,
  BriefcaseBusiness,
  Presentation,
  Search,
  BarChart3,
  Gauge,
  ClipboardCheck,
  Building2,
  Package,
  GripVertical,
  Pencil,
  Check,
  RotateCcw,
  Eye,
  EyeOff,
  Sparkles,
  ExternalLink,
  MessageSquare,
  MapPin as MapPinIcon,
  GraduationCap
} from 'lucide-react';
import { Store as StoreIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { cn } from '../lib/utils';
import { OfflineIndicator } from './OfflineIndicator';
import NotificationBell from './NotificationBell';
import InstallPWAButton from './InstallPWAButton';
import GlobalCommandPalette from './GlobalCommandPalette';
import { toast } from 'sonner';

// Rôles pour lesquels un admin peut masquer une entrée de menu (jamais admin/super_admin).
const HIDEABLE_ROLES = [
  { key: 'commercial', label: 'Commercial' },
  { key: 'commerciale', label: 'Commerciale' },
  { key: 'assistante_commerciale', label: 'Assistante Com.' },
  { key: 'prestataire', label: 'Prestataire' },
];

const Logo = ({ dark = true }) => (
  <div className="w-full" data-testid="sidebar-logo">
    <div className={cn('font-display font-extrabold tracking-tight leading-none text-white', dark ? 'text-[34px]' : 'text-[26px]')}>
      CAPE<span className="text-orange">X</span>
    </div>
    <div className="font-body font-bold text-orange uppercase mt-0.5" style={{ fontSize: dark ? 9 : 8, letterSpacing: '0.34em' }}>
      Wasabi Business
    </div>
  </div>
);

const NAV_FAMILIES = [
  { key: 'pilotage', title: 'Pilotage', routes: ['/dashboard', '/simulation/new', '/history', '/announcements', '/ca-corner', '/carte-corners', '/rapports', '/presentation', '/cockpit'] },
  { key: 'commerce', title: 'Commerce', routes: ['/capex-sales', '/golden-info', '/business-commercial', '/performance', '/primes', '/assistant'] },
  { key: 'projets', title: 'Projets corners', routes: ['/croquis-agenceur', '/prerequis-techniques', '/contract-requests', '/chantiers', '/planning-chantiers', '/dossiers-agenceur', '/pv-livraison', '/capex-hg', '/approvisionnement-ccd', '/admin/referentiels'] },
  { key: 'reseau', title: 'Réseau', routes: ['/messages', '/guide', '/guide-pv'] },
  { key: 'formation', title: 'Formation', routes: ['/formation'] },
  { key: 'autres', title: 'Autres', routes: [] },
];
const FAMILY_OF = (() => { const m = {}; NAV_FAMILIES.forEach(f => f.routes.forEach(r => { m[r] = f.key; })); return m; })();

const NavItem = ({ to, icon: Icon, label, onClick, variant = 'default', badge = 0 }) => {
  const location = useLocation();
  const isActive = location.pathname === to || location.pathname.startsWith(to + '/');
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={cn(
        'flex items-center gap-3 px-3 h-[38px] rounded-nav transition-colors duration-150',
        isActive
          ? 'bg-graphite-600 text-white font-semibold'
          : 'text-ondark-2 hover:bg-graphite-700 hover:text-white'
      )}
    >
      <Icon className={cn('w-[18px] h-[18px] shrink-0', isActive ? 'text-orange' : '')} strokeWidth={1.8} />
      <span className="text-sm truncate">{label}</span>
      {badge > 0 && (
        <span className="ml-auto flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-orange text-orange-on text-[11px] font-bold">{badge}</span>
      )}
    </NavLink>
  );
};

const MobileNavItem = ({ to, icon: Icon, label, badge = 0, onClick }) => {
  const location = useLocation();
  const isActive = to ? (location.pathname === to || location.pathname.startsWith(to + '/')) : false;

  const content = (
    <>
      <div className="relative">
        <Icon className={cn('w-5 h-5', isActive && 'scale-110')} />
        {badge > 0 && (
          <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </div>
      <span className="text-[10px] font-medium leading-tight text-center truncate max-w-[64px]">{label}</span>
    </>
  );

  if (onClick) {
    return (
      <button
        onClick={onClick}
        className={cn(
          'flex flex-col items-center gap-0.5 py-1.5 px-2 rounded-lg transition-colors duration-200 min-w-[64px]',
          isActive ? 'text-blue-600 dark:text-blue-400' : 'text-slate-500 dark:text-slate-400'
        )}
        data-testid={`mobile-nav-${label}`}
      >
        {content}
      </button>
    );
  }

  return (
    <NavLink
      to={to}
      className={cn(
        'flex flex-col items-center gap-0.5 py-1.5 px-2 rounded-lg transition-colors duration-200 min-w-[64px]',
        isActive ? 'text-blue-600 dark:text-blue-400' : 'text-slate-500 dark:text-slate-400'
      )}
      data-testid={`mobile-nav-${to.replace(/^\//, '')}`}
    >
      {content}
    </NavLink>
  );
};

export const AppLayout = ({ children }) => {
  const { user, logout, isAdmin, token } = useAuth();
  const navigate = useNavigate();
  const { t, toggleLanguage, language } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [impOpen, setImpOpen] = React.useState(false);
  const [pendingCounts, setPendingCounts] = React.useState({});
  const [chatUnread, setChatUnread] = React.useState(0);
  const [croquisPending, setCroquisPending] = React.useState(0);

  const isPrestataire = user?.role === 'prestataire';
  const [cornerAccess, setCornerAccess] = React.useState(true);
  const [caCornerAccess, setCaCornerAccess] = React.useState(false);
  const [csSalesAccess, setCsSalesAccess] = React.useState(false);
  const [roleSectionAccess, setRoleSectionAccess] = React.useState({});

  // Ordre GLOBAL du menu (défini par admin/super_admin, appliqué à tous)
  const [sidebarOrder, setSidebarOrder] = React.useState([]);
  const [hiddenByRole, setHiddenByRole] = React.useState({});
  const [editOrder, setEditOrder] = React.useState(false);
  const [draftOrder, setDraftOrder] = React.useState([]);
  const [draftHidden, setDraftHidden] = React.useState({});
  const [rolePanelTo, setRolePanelTo] = React.useState(null);
  const [savingOrder, setSavingOrder] = React.useState(false);
  const dragItem = React.useRef(null);

  React.useEffect(() => {
    if (token) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sidebar-order`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(r => r.ok ? r.json() : null).then(d => {
        if (d && Array.isArray(d.order)) setSidebarOrder(d.order);
        if (d && d.hidden && typeof d.hidden === 'object') setHiddenByRole(d.hidden);
      }).catch(() => {});
    }
  }, [token]);

  React.useEffect(() => {
    if (token && !isPrestataire) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ca-corner/access`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(r => r.ok ? r.json() : null).then(d => {
        if (d) setCaCornerAccess(!!d.allowed);
      }).catch(() => {});
    }
  }, [token, isPrestataire]);

  React.useEffect(() => {
    if (token && !isPrestataire) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/capex-sales/access`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(r => { setCsSalesAccess(r.ok); }).catch(() => setCsSalesAccess(false));
    }
  }, [token, isPrestataire]);

  React.useEffect(() => {
    if (token) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/role-section-access`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(r => r.ok ? r.json() : null).then(d => {
        if (d && d.role_section_access && typeof d.role_section_access === 'object') setRoleSectionAccess(d.role_section_access);
      }).catch(() => {});
    }
  }, [token]);

  React.useEffect(() => {
    if (isPrestataire && token) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dossiers-agenceur-access`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(r => r.ok ? r.json() : null).then(d => {
        if (d) setCornerAccess(d.is_allowed);
      }).catch(() => {});
    }
  }, [isPrestataire, token]);

  React.useEffect(() => {
    if (isAdmin && token) {
      const fetchPending = async () => {
        try {
          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pending-counts`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            setPendingCounts(data || {});
          }
        } catch {}
      };
      fetchPending();
      const interval = setInterval(fetchPending, 30000);
      return () => clearInterval(interval);
    }
  }, [isAdmin, token]);

  React.useEffect(() => {
    if (token && !isPrestataire) {
      const fetchChat = async () => {
        try {
          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/simulations/chat/unread-summary`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            setChatUnread(data?.total || 0);
          }
        } catch {}
      };
      fetchChat();
      const interval = setInterval(fetchChat, 30000);
      return () => clearInterval(interval);
    }
  }, [token, isPrestataire]);

  React.useEffect(() => {
    if (token && isAdmin) {
      const fetchCroquis = async () => {
        try {
          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/croquis/pending-count`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) { const data = await res.json(); setCroquisPending(data?.count || 0); }
        } catch {}
      };
      fetchCroquis();
      const interval = setInterval(fetchCroquis, 30000);
      return () => clearInterval(interval);
    }
  }, [token, isAdmin]);

  const navItems = isPrestataire 
    ? [
        { to: '/chantiers', icon: HardHat, label: 'Pilotage Chantier', variant: 'default' },
        { to: '/pv-livraison', icon: ClipboardCheck, label: 'PV de Livraison' },
        ...(cornerAccess ? [{ to: '/dossiers-agenceur', icon: Send, label: 'Commande Corner' }] : []),
      ]
    : [
        { to: '/dashboard', icon: LayoutDashboard, label: t('dashboard') },
        { to: '/messages', icon: MessageSquare, label: 'Messages', badge: chatUnread },
        { to: '/rapports', icon: BarChart3, label: 'Rapports', variant: 'orange' },
        { to: '/croquis-agenceur', icon: Presentation, label: 'Croquis Agenceur', variant: 'orange', badge: croquisPending },
        { to: '/simulation/new', icon: PlusCircle, label: t('newSimulation'), badge: pendingCounts.simulations || 0 },
        { to: '/golden-info', icon: Trophy, label: "Info d'Or", variant: 'golden', badge: pendingCounts.golden_info || 0 },
        { to: '/announcements', icon: Megaphone, label: "Annonces ALL", variant: 'emerald', badge: pendingCounts.announcements || 0 },
        { to: '/chantiers', icon: HardHat, label: 'Pilotage Chantier', badge: pendingCounts.chantiers || 0 },
        { to: '/pv-livraison', icon: ClipboardCheck, label: 'PV de Livraison' },
        { to: '/prerequis-techniques', icon: ShieldCheck, label: 'Prérequis Techniques', variant: 'amber' },
        { to: '/contract-requests', icon: FileSignature, label: 'Demande de Contrat', badge: pendingCounts.contracts || 0 },
      ];

  if (isAdmin) {
    // Insert Commande Corner right after Demande de Contrat
    navItems.push({ to: '/dossiers-agenceur', icon: Send, label: 'Commande Corner' });
  }

  // Business Commercial — commercial/commerciale/admin/super_admin/assistante_commerciale
  // Accès : Direction (admin / super admin) et commerciaux
  const businessRoles = new Set(['admin', 'super_admin', 'commercial', 'commerciale']);
  if (!isPrestataire && businessRoles.has(user?.role)) {
    navItems.push({ to: '/business-commercial', icon: BriefcaseBusiness, label: 'Business Commercial', variant: 'indigo' });
  }

  // Planning Chantiers — visible commercial/commerciale/admin/super_admin/assistante_commerciale
  const planningRoles = new Set(['commercial', 'commerciale', 'admin', 'super_admin', 'assistante_commerciale']);
  if (!isPrestataire && planningRoles.has(user?.role)) {
    navItems.push({ to: '/planning-chantiers', icon: CalendarDays, label: 'Planning chantiers', variant: 'amber' });
  }

  // Performance Commerciaux — super_admin/admin/commercial/commerciale
  const perfRoles = new Set(['super_admin', 'admin', 'commercial', 'commerciale']);
  if (!isPrestataire && perfRoles.has(user?.role)) {
    navItems.push({ to: '/performance', icon: Trophy, label: 'Performance', variant: 'golden' });
  }

  // CAPEX SALES — visible pour les utilisateurs autorisés (liste blanche côté serveur)
  if (csSalesAccess && !isPrestataire) {
    navItems.push({ to: '/capex-sales', icon: Building2, label: 'CAPEX SALES', variant: 'orange' });
  }

  navItems.push({ to: '/history', icon: History, label: t('history') });
  navItems.push({ to: '/guide', icon: BookOpen, label: 'Guide' });
  if (!isPrestataire) navItems.push({ to: '/formation', icon: GraduationCap, label: 'Formations/Vidéos', variant: 'orange' });
  navItems.push({ to: '/guide-pv', icon: FileSignature, label: 'Présentation PV', variant: 'indigo' });
  if (user?.role === 'super_admin') {
    if ((user?.email || '').trim().toLowerCase() === 'c.perez@wasabicorner.fr') {
      navItems.push({ to: '/primes', icon: DollarSign, label: 'Primes', variant: 'default' });
    }
    navItems.push({ to: '/presentation', icon: Presentation, label: 'Présentation', variant: 'orange' });
  }

  if (caCornerAccess && !isPrestataire) {
    navItems.push({ to: '/ca-corner', icon: StoreIcon, label: 'CA Corner', variant: 'indigo' });
  }

  navItems.push({ to: '/assistant', icon: Sparkles, label: 'Assistant CAPEX', variant: 'orange' });
  if (caCornerAccess && !isPrestataire) {
    navItems.push({ to: '/carte-corners', icon: MapPinIcon, label: 'Carte des Corners', variant: 'indigo' });
  }

  if (isAdmin) {
    navItems.push({ to: '/capex-hg', icon: Building2, label: 'Capex HG', variant: 'indigo' });
    navItems.push({ to: '/approvisionnement-ccd', icon: Package, label: 'Approvisionnement CCD', variant: 'indigo' });
    navItems.push({ to: '/admin/referentiels', icon: Building2, label: 'Référentiels', variant: 'indigo' });
    navItems.push({ to: '/cockpit', icon: Gauge, label: 'Cockpit Direction', variant: 'indigo' });
    navItems.push({ to: '/admin', icon: Shield, label: t('admin') });
  }

  // --- Registre des sections attribuables (utilisé par la config d'accès PAR RÔLE) ---
  const grantableRegistry = {
    '/dashboard': { to: '/dashboard', icon: LayoutDashboard, label: t('dashboard') },
    '/simulation/new': { to: '/simulation/new', icon: PlusCircle, label: t('newSimulation') },
    '/rapports': { to: '/rapports', icon: BarChart3, label: 'Rapports', variant: 'orange' },
    '/croquis-agenceur': { to: '/croquis-agenceur', icon: Presentation, label: 'Croquis Agenceur', variant: 'orange' },
    '/golden-info': { to: '/golden-info', icon: Trophy, label: "Info d'Or", variant: 'golden' },
    '/announcements': { to: '/announcements', icon: Megaphone, label: 'Annonces ALL', variant: 'emerald' },
    '/chantiers': { to: '/chantiers', icon: HardHat, label: 'Pilotage Chantier' },
    '/pv-livraison': { to: '/pv-livraison', icon: ClipboardCheck, label: 'PV de Livraison' },
    '/prerequis-techniques': { to: '/prerequis-techniques', icon: ShieldCheck, label: 'Prérequis Techniques', variant: 'amber' },
    '/contract-requests': { to: '/contract-requests', icon: FileSignature, label: 'Demande de Contrat' },
    '/dossiers-agenceur': { to: '/dossiers-agenceur', icon: Send, label: 'Commande Corner' },
    '/business-commercial': { to: '/business-commercial', icon: BriefcaseBusiness, label: 'Business Commercial', variant: 'indigo' },
    '/planning-chantiers': { to: '/planning-chantiers', icon: CalendarDays, label: 'Planning chantiers', variant: 'amber' },
    '/performance': { to: '/performance', icon: Trophy, label: 'Performance', variant: 'golden' },
    '/ca-corner': { to: '/ca-corner', icon: StoreIcon, label: 'CA Corner', variant: 'indigo' },
    '/carte-corners': { to: '/carte-corners', icon: MapPinIcon, label: 'Carte des Corners', variant: 'indigo' },
    '/history': { to: '/history', icon: History, label: t('history') },
    '/guide': { to: '/guide', icon: BookOpen, label: 'Guide' },
    '/guide-pv': { to: '/guide-pv', icon: FileSignature, label: 'Présentation PV', variant: 'indigo' },
    '/assistant': { to: '/assistant', icon: Sparkles, label: 'Assistant CAPEX', variant: 'orange' },
  };
  // --- Défaut PAR RÔLE (configuré par l'admin ; appliqué avant la surcharge utilisateur) ---
  const roleCfg = (!isPrestataire && user?.role && user?.role !== 'super_admin') ? roleSectionAccess[user.role] : null;
  if (roleCfg && typeof roleCfg === 'object') {
    for (let i = navItems.length - 1; i >= 0; i--) {
      const to = navItems[i].to;
      if (MANAGEABLE_ROUTES.has(to) && roleCfg[to] === false) navItems.splice(i, 1);
    }
    const presentR = new Set(navItems.map(i => i.to));
    Object.keys(roleCfg).forEach(to => {
      if (roleCfg[to] === true && MANAGEABLE_ROUTES.has(to) && !presentR.has(to) && grantableRegistry[to]) {
        navItems.push(grantableRegistry[to]);
      }
    });
  }

  // --- Masquage par rôle (appliqué à l'utilisateur courant ; admins jamais masqués) ---
  const visibleNavItems = navItems.filter(it => !((hiddenByRole[it.to] || []).includes(user?.role)));

  // --- Application de l'ordre global personnalisé (réordonnancement) ---
  const byTo = new Map(navItems.map(i => [i.to, i]));
  const orderMap = new Map(sidebarOrder.map((to, i) => [to, i]));
  const orderedNavItems = visibleNavItems
    .map((it, idx) => ({ it, idx }))
    .sort((a, b) => {
      const ka = orderMap.has(a.it.to) ? orderMap.get(a.it.to) : 1000 + a.idx;
      const kb = orderMap.has(b.it.to) ? orderMap.get(b.it.to) : 1000 + b.idx;
      return ka - kb;
    })
    .map(x => x.it);

  // En mode édition, l'admin voit TOUTES les entrées (y compris celles masquées pour d'autres rôles).
  const orderedAllForEdit = navItems
    .map((it, idx) => ({ it, idx }))
    .sort((a, b) => {
      const ka = orderMap.has(a.it.to) ? orderMap.get(a.it.to) : 1000 + a.idx;
      const kb = orderMap.has(b.it.to) ? orderMap.get(b.it.to) : 1000 + b.idx;
      return ka - kb;
    })
    .map(x => x.it);

  const startEditOrder = () => {
    setDraftOrder(orderedAllForEdit.map(i => i.to));
    setDraftHidden({ ...hiddenByRole });
    setRolePanelTo(null);
    setEditOrder(true);
  };
  const onDragStart = (i) => { dragItem.current = i; };
  const onDragOver = (e, i) => {
    e.preventDefault();
    const from = dragItem.current;
    if (from === null || from === undefined || from === i) return;
    setDraftOrder(prev => {
      const next = [...prev];
      const [m] = next.splice(from, 1);
      next.splice(i, 0, m);
      return next;
    });
    dragItem.current = i;
  };
  const onDragEnd = () => { dragItem.current = null; };
  const toggleRoleHidden = (to, roleKey) => {
    setDraftHidden(prev => {
      const cur = new Set(prev[to] || []);
      if (cur.has(roleKey)) cur.delete(roleKey); else cur.add(roleKey);
      const next = { ...prev };
      if (cur.size) next[to] = Array.from(cur); else delete next[to];
      return next;
    });
  };
  const persistOrder = async (order, hidden, msg) => {
    setSavingOrder(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sidebar-order`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ order, hidden }),
      });
      if (!res.ok) throw new Error();
      setSidebarOrder(order);
      setHiddenByRole(hidden);
      setEditOrder(false);
      setRolePanelTo(null);
      toast.success(msg);
    } catch { toast.error("Erreur lors de l'enregistrement du menu"); }
    finally { setSavingOrder(false); }
  };
  const saveOrder = () => persistOrder(draftOrder, draftHidden, 'Menu enregistré pour tous les utilisateurs');
  const resetOrder = () => persistOrder([], {}, 'Menu réinitialisé (par défaut)');

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-graphite-700 bg-graphite-900 px-5 py-6" data-testid="sidebar-nav">
          <Logo />
          
          <nav className="flex flex-1 flex-col gap-2 mt-4 min-h-0 overflow-y-auto" data-testid="sidebar-nav-list">
            {isAdmin && (
              <div className="flex items-center px-1 pb-1">
                {!editOrder ? (
                  <button onClick={startEditOrder}
                          className="ml-auto flex items-center gap-1.5 text-xs font-medium text-ondark-3 hover:text-orange transition-colors"
                          data-testid="sidebar-reorder-btn">
                    <Pencil className="w-3.5 h-3.5" /> Réorganiser
                  </button>
                ) : (
                  <div className="flex items-center gap-2 w-full">
                    <span className="text-xs font-semibold text-indigo-600">Glissez pour réordonner</span>
                    <div className="ml-auto flex items-center gap-1">
                      <button onClick={resetOrder} disabled={savingOrder} title="Réinitialiser (ordre par défaut)"
                              className="p-1.5 rounded text-slate-400 hover:text-amber-600 hover:bg-amber-50 disabled:opacity-50" data-testid="sidebar-reset-btn"><RotateCcw className="w-4 h-4" /></button>
                      <button onClick={() => setEditOrder(false)} title="Annuler"
                              className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid="sidebar-cancel-btn"><X className="w-4 h-4" /></button>
                      <button onClick={saveOrder} disabled={savingOrder} title="Enregistrer"
                              className="p-1.5 rounded text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50" data-testid="sidebar-save-btn"><Check className="w-4 h-4" /></button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {editOrder ? (
              draftOrder.map((to, i) => {
                const item = byTo.get(to);
                if (!item) return null;
                const Icon = item.icon;
                const hiddenRoles = draftHidden[to] || [];
                return (
                  <div key={to} className="shrink-0 rounded-lg border border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden"
                       data-testid={`sidebar-reorder-item-${to.replace(/^\//, '')}`}>
                    <div draggable
                         onDragStart={() => onDragStart(i)}
                         onDragOver={(e) => onDragOver(e, i)}
                         onDragEnd={onDragEnd}
                         className="flex items-center gap-2 px-3 py-2.5 cursor-grab active:cursor-grabbing hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors">
                      <GripVertical className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <Icon className="w-5 h-5 text-slate-500 flex-shrink-0" />
                      <span className="font-medium text-sm truncate flex-1 text-slate-700 dark:text-slate-200">{item.label}</span>
                      <button onClick={() => setRolePanelTo(rolePanelTo === to ? null : to)}
                              title="Masquer par rôle"
                              className={`flex items-center gap-1 flex-shrink-0 ${hiddenRoles.length ? 'text-amber-600' : 'text-slate-300 hover:text-slate-500'}`}
                              data-testid={`sidebar-hide-btn-${to.replace(/^\//, '')}`}>
                        {hiddenRoles.length ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        {hiddenRoles.length > 0 && <span className="text-[10px] font-bold">{hiddenRoles.length}</span>}
                      </button>
                    </div>
                    {rolePanelTo === to && (
                      <div className="px-3 pb-2.5 pt-1.5 border-t border-dashed border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/40">
                        <p className="text-[11px] text-slate-400 mb-1.5">Masquer cette entrée pour :</p>
                        <div className="grid grid-cols-2 gap-1.5">
                          {HIDEABLE_ROLES.map(r => {
                            const isHidden = hiddenRoles.includes(r.key);
                            return (
                              <button key={r.key} onClick={() => toggleRoleHidden(to, r.key)}
                                      className={`text-[11px] px-2 py-1 rounded border text-left transition-colors ${isHidden ? 'bg-amber-100 dark:bg-amber-900/30 border-amber-300 text-amber-700 dark:text-amber-400 line-through' : 'border-slate-200 dark:border-slate-600 text-slate-500 hover:border-slate-300'}`}
                                      data-testid={`sidebar-hide-role-${to.replace(/^\//, '')}-${r.key}`}>
                                {r.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              NAV_FAMILIES.map(group => {
                const items = orderedNavItems.filter(it => it.to !== '/admin' && (FAMILY_OF[it.to] || 'autres') === group.key);
                if (items.length === 0) return null;
                return (
                  <div key={group.key} className="flex flex-col gap-1">
                    <p className="px-3 pt-3 pb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-ondark-4">{group.title}</p>
                    {items.map((item) => (<NavItem key={item.to} {...item} />))}
                  </div>
                );
              })
            )}
            {/* Croquis Corner — outil externe d'agencement GMS */}
            <a
              href="https://croquiscorner.wasabicorner.fr/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-3 h-[38px] rounded-nav transition-colors duration-150 text-ondark-2 hover:bg-graphite-700 hover:text-white"
              data-testid="sidebar-croquis-link"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_9e50721a-7736-4624-b2e1-614423eb8a0a/artifacts/q8p0nhzp_image.png"
                alt="Croquis Corner"
                className="w-[18px] h-[18px] object-contain rounded"
              />
              <span className="text-sm">Croquis Corner</span>
              <ExternalLink className="w-3.5 h-3.5 ml-auto text-ondark-3" />
            </a>
          </nav>

          {/* Install PWA invitation (auto-hidden when already installed or unsupported) */}
          <InstallPWAButton variant="sidebar" />

          <div className="flex items-center gap-2 flex-shrink-0">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 p-3 rounded-card bg-graphite-800 hover:bg-graphite-700 transition-colors flex-1 min-w-0 text-left" data-testid="user-menu-trigger">
                  <div className="w-11 h-11 rounded-full bg-orange flex items-center justify-center shrink-0">
                    <span className="text-orange-on font-bold text-base">
                      {user?.name?.split(' ').map(n => n.charAt(0).toUpperCase()).join('').slice(0, 2)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
                    <p className="text-xs text-ondark-3 truncate capitalize">
                      {user?.role === 'super_admin' ? 'Super Admin' : user?.role}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-ondark-3 shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" align="start" className="w-56">
                <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="user-menu-profile">
                  <User className="w-4 h-4 mr-2" /> {t('profile')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={toggleLanguage} data-testid="user-menu-language">
                  <Globe className="w-4 h-4 mr-2" /> {language === 'fr' ? 'English' : 'Français'}
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="user-menu-admin">
                    <Shield className="w-4 h-4 mr-2" /> {t('admin')}
                  </DropdownMenuItem>
                )}
                {isAdmin && (
                  <DropdownMenuItem onClick={startEditOrder} data-testid="user-menu-reorder">
                    <Pencil className="w-4 h-4 mr-2" /> Réorganiser le menu
                  </DropdownMenuItem>
                )}
                {user?.role === 'super_admin' && (
                  <DropdownMenuItem onClick={() => setImpOpen(true)} data-testid="user-menu-impersonate">
                    <Eye className="w-4 h-4 mr-2" /> Voir comme…
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-danger focus:text-danger" data-testid="user-menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> {t('logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {/* Notification Bell - Desktop */}
            <NotificationBell />
          </div>

          {/* Quick search (⌘+K) for desktop sidebar */}
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent('open-global-search'))}
            className="hidden lg:flex items-center gap-2 px-3 h-10 rounded-ctrl border border-graphite-600 bg-graphite-800 text-ondark-3 hover:bg-graphite-700 transition-colors text-sm w-full"
            data-testid="sidebar-search-trigger"
          >
            <Search className="w-4 h-4" />
            <span className="flex-1 text-left">Rechercher…</span>
            <kbd className="inline-flex items-center gap-1 rounded border border-graphite-500 bg-graphite-900 px-1.5 py-0.5 text-[10px] font-mono text-ondark-3">
              ⌘K
            </kbd>
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-40 flex h-16 items-center gap-x-4 border-b border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 backdrop-blur px-4">
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="-ml-2">
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-0 bg-graphite-900 border-graphite-700">
            <div className="flex flex-col h-full">
              <div className="p-6 border-b border-graphite-700">
                <Logo />
              </div>
              
              <nav className="flex-1 overflow-y-auto p-4 space-y-1">
                {NAV_FAMILIES.map(group => {
                  const items = orderedNavItems.filter(it => it.to !== '/admin' && (FAMILY_OF[it.to] || 'autres') === group.key);
                  if (items.length === 0) return null;
                  return (
                    <div key={group.key} className="flex flex-col gap-1">
                      <p className="px-3 pt-3 pb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-ondark-4">{group.title}</p>
                      {items.map((item) => (
                        <NavItem key={item.to} {...item} onClick={() => setMobileMenuOpen(false)} />
                      ))}
                    </div>
                  );
                })}
                {/* Croquis Corner — outil externe d'agencement GMS */}
                <a
                  href="https://croquiscorner.wasabicorner.fr/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 h-[38px] rounded-nav transition-colors duration-150 text-ondark-2 hover:bg-graphite-700 hover:text-white"
                  data-testid="sidebar-croquis-link-mobile"
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_9e50721a-7736-4624-b2e1-614423eb8a0a/artifacts/q8p0nhzp_image.png"
                    alt="Croquis Corner"
                    className="w-[18px] h-[18px] object-contain rounded"
                  />
                  <span className="text-sm">Croquis Corner</span>
                  <ExternalLink className="w-3.5 h-3.5 ml-auto text-ondark-3" />
                </a>
              </nav>

              <div className="border-t border-graphite-700 p-4 space-y-1">
                <NavItem to="/profile" icon={User} label={t('profile')} onClick={() => setMobileMenuOpen(false)} />
                {isAdmin && <NavItem to="/admin" icon={Shield} label={t('admin')} onClick={() => setMobileMenuOpen(false)} />}
                <button
                  onClick={() => { toggleLanguage(); setMobileMenuOpen(false); }}
                  className="flex items-center gap-3 px-3 h-[38px] rounded-nav text-ondark-2 hover:bg-graphite-700 hover:text-white w-full transition-colors duration-150"
                >
                  <Globe className="w-[18px] h-[18px]" strokeWidth={1.8} />
                  <span className="text-sm">{language === 'fr' ? 'English' : 'Français'}</span>
                </button>
                <button
                  onClick={() => { logout(); setMobileMenuOpen(false); }}
                  className="flex items-center gap-3 px-3 h-[38px] rounded-nav text-ondark-2 hover:bg-graphite-700 hover:text-white w-full transition-colors duration-150"
                >
                  <LogOut className="w-[18px] h-[18px]" strokeWidth={1.8} />
                  <span className="text-sm">{t('logout')}</span>
                </button>
              </div>
            </div>
          </SheetContent>
        </Sheet>

        <Logo dark={false} />
        
        {/* User Avatar - Mobile */}
        <div className="ml-auto flex items-center gap-2">
          <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-800">
            <div className="w-8 h-8 rounded-full bg-orange flex items-center justify-center">
              <span className="text-orange-on font-bold text-sm">
                {user?.name?.split(' ').map(n => n.charAt(0).toUpperCase()).join('').slice(0, 2)}
              </span>
            </div>
            <div className="hidden sm:block pr-1">
              <p className="text-xs font-medium text-slate-700 dark:text-slate-200 leading-tight">
                {user?.name?.split(' ')[0]}
              </p>
              <p className="text-[10px] text-slate-500 dark:text-slate-400 leading-tight capitalize">
                {user?.role === 'super_admin' ? 'Super Admin' : user?.role}
              </p>
            </div>
          </div>
          <GlobalCommandPalette />
          <NotificationBell />
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:pl-72 pb-20 lg:pb-0 overflow-x-hidden">
        <ImpersonationBanner />
        <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
          {children}
        </div>
      </main>
      <ImpersonateDialog open={impOpen} onClose={() => setImpOpen(false)} />

      {/* Mobile Bottom Navigation */}
      <nav className="mobile-nav lg:hidden">
        <div className="flex items-center justify-around py-1">
          {orderedNavItems.slice(0, 4).map((item) => (
            <MobileNavItem key={item.to} {...item} />
          ))}
          <MobileNavItem
            label={t('more') || 'Plus'}
            icon={Menu}
            onClick={() => setMobileMenuOpen(true)}
            badge={Object.values(pendingCounts).slice(4).reduce((a, b) => a + (b || 0), 0)}
          />
        </div>
      </nav>

      {/* Offline Indicator */}
      <OfflineIndicator />
    </div>
  );
};
